import { apiFetch } from '@carouselgg/auth/client'
import type { TrustScoreTip } from '@carouselgg/auth/types'

export interface GameItem {
  id: string
  name: string
  slug: string
  logo_url: string
}

export interface DiscoverSeason {
  id: string
  competition_type: 'league' | 'tournament'
  organization_id: string
  org_name: string
  org_slug: string
  org_logo_url: string
  org_primary_color: string
  game_id: string
  game_name: string
  game_slug: string
  game_logo_url: string
  division_id: string
  division_name: string
  division_slug: string
  name: string
  slug: string
  description: string
  status: string
  starts_at: string | null
  ends_at: string | null
  registration_opens_at: string | null
  registration_closes_at: string | null
  /** Where it's played; in-person events can take walk-up players. */
  venue_mode?: 'online' | 'in_person'
  min_rank_display: string
  max_rank_display: string
  min_teams: number
  max_teams: number | null
  prize_pool: number
  format: string
  free_agent_mode: string
  min_trust_score: number | null
  registered_teams: number
  /** Set while Carousel staff are featuring it; higher takes the spotlight. */
  featured_priority: number | null
  /** Players per side for the game; 1 = a solo game. */
  team_size: number
  /** Game's roster cap, starters plus subs. 0 = no cap. */
  max_roster_size: number
  /** Entered teams that are open to free agents. */
  recruiting_teams: number
  /** Empty starting places on those teams. */
  open_starting_spots: number
  /** Empty bench places on those teams under the roster cap (0 when uncapped). */
  open_bench_spots: number
}

export interface DiscoverResponse {
  seasons: DiscoverSeason[]
  total: number
}

export async function getGamesWithOpenSeasons(): Promise<GameItem[]> {
  return apiFetch<GameItem[]>('/compete/games')
}

export async function discoverSeasons(gameId?: string, limit = 50, offset = 0): Promise<DiscoverResponse> {
  const params = new URLSearchParams()
  if (gameId) params.set('game_id', gameId)
  params.set('limit', String(limit))
  params.set('offset', String(offset))
  return apiFetch<DiscoverResponse>(`/compete/discover?${params}`)
}

export async function discoverUpcomingSeasons(gameId?: string, limit = 20, offset = 0): Promise<DiscoverResponse> {
  const params = new URLSearchParams()
  if (gameId) params.set('game_id', gameId)
  params.set('limit', String(limit))
  params.set('offset', String(offset))
  return apiFetch<DiscoverResponse>(`/compete/upcoming?${params}`)
}

/**
 * The signed-in player's Trust Score for a game as competition minimums judge
 * it — per game where per-game eligibility is on, overall where it is off — and
 * ways to raise it, biggest gain first.
 */
export interface TrustStanding {
  game_id: string
  scope: 'game' | 'overall'
  score: number
  /** Decent overall, thin history in this game. */
  low_game_specific: boolean
  tips: TrustScoreTip[]
}

export async function getTrustStanding(gameId: string): Promise<TrustStanding> {
  return apiFetch<TrustStanding>(`/compete/trust-standing?game_id=${encodeURIComponent(gameId)}`)
}
